'use client'

// libraries
import clsx from 'clsx'
import { useState, useEffect } from 'react'

// components
import Section from '/react4xp/components/Section'
import RoundedBox from '/react4xp/components/RoundedBox'
import Input from '/react4xp/components/Form/Input'
import Portal from '/react4xp/components/Portal'
import ContactCard, { ContactCardProps } from '/react4xp/components/ContactCard'
import { useResultsAnimation } from '/react4xp/components/Animations/useResultsAnimation'

// svg
import { ChevronDown, Close, CloseLight, Loading } from '/react4xp/components/Icons/Ux'

// css
import './index.scss'

// types
interface SearchEmployeesProps {
    className?: string
    mockData?: ContactCardProps[]
    initialValues?: {
        search?: string
        administrative?: string[]
        scientific?: string[]
        place?: string[]
    }
    searchBox?: {
        title?: string
        searchBar?: {
            placeholder?: string
            cta?: string
        }
        ctaText?: string
        ctas?: {
            label?: string
            url?: string
            external?: boolean
        }[]
    }
    howManyPerPage?: number
    singleStrings?: {
        showing?: string
        results?: string
        filterTitles?: {
            administrative?: {
                label?: string
                viewAll?: string
            }
            scientific?: {
                label?: string
                viewAll?: string
            }
            place?: {
                label?: string
                viewAll?: string
            }
        }
        filterCtaMobile?: {
            filter?: string
            filtering?: string
            activeFilters?: string
            apply?: string
        }
        loadMore?: string
        loading?: string
        clearSearch?: string
        teacherPrefix: string
    }
    services?: {
        getEmployees?: string
    }
    filters?: {
        administrative?: string[]
        scientific?: string[]
        place?: string[]
    }
}

export default function SearchEmployees({
    className,
    mockData,
    initialValues,
    searchBox,
    howManyPerPage = 20,
    singleStrings,
    services,
    filters
}: SearchEmployeesProps) {

    // state for mobile filters modal
    const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false)

    // mobile filters handlers
    const openMobileFilters = () => {
        setIsMobileFiltersOpen(true)

        setTimeout(() => {
            document.querySelector('#portal')?.classList.add('is-open')
        }, 100)
    }

    const closeMobileFilters = () => {
        document.querySelector('#portal')?.classList.remove('is-open')
        setTimeout(() => {
            setIsMobileFiltersOpen(false)
        }, 400)
    }

    // handle escape key to close mobile filters
    useEffect(() => {
        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isMobileFiltersOpen) {
                closeMobileFilters()
            }
        }

        if (isMobileFiltersOpen) {
            document.addEventListener('keydown', handleEscapeKey)
            // Prevent body scroll when modal is open
            document.body.style.overflow = 'hidden'
        }

        return () => {
            document.removeEventListener('keydown', handleEscapeKey)
            document.body.style.overflow = 'unset'
        }
    }, [isMobileFiltersOpen])

    // Parse filters to detect "Lærerutdanning" subcategories
    const parseFiltersWithSubcategories = (filtersList: string[]) => {
        const laerutdanningSubcategories: { label: string; fullValue: string }[] = []
        const standalone: string[] = []
        let hasStandaloneLaerutdanning = false
        
        filtersList.forEach(item => {
            if (item.startsWith(`${singleStrings?.teacherPrefix},`)) {
                const subcategory = item.substring(`${singleStrings?.teacherPrefix},`.length).trim()
                laerutdanningSubcategories.push({
                    label: subcategory,
                    fullValue: item
                })
            } else if (item === singleStrings?.teacherPrefix) {
                hasStandaloneLaerutdanning = true
            } else {
                standalone.push(item)
            }
        })
        
        const result: (string | { label: string; subcategories: { label: string; fullValue: string }[] })[] = []
        
        // If we have subcategories OR standalone Lærerutdanning, create the parent with all subcategories
        if (laerutdanningSubcategories.length > 0 || hasStandaloneLaerutdanning) {
            // Add standalone "Lærerutdanning" as the first subcategory if it exists
            if (hasStandaloneLaerutdanning) {
                laerutdanningSubcategories.unshift({
                    label: singleStrings?.teacherPrefix as string,
                    fullValue: singleStrings?.teacherPrefix as string
                })
            }
            
            result.push({
                label: singleStrings?.teacherPrefix as string,
                subcategories: laerutdanningSubcategories
            })
        }
        
        return [...result, ...standalone]
    }

    const administrativeOptions = filters?.administrative || []
    const scientificOptions = parseFiltersWithSubcategories(filters?.scientific || [])
    const placeOptions = filters?.place || []

    // state for expanded sidebar items - all expanded by default
    const [expandedItems, setExpandedItems] = useState<string[]>([
        'administrative',
        'scientific',
        'place'
    ])

    // state for expanded subcategories
    const [expandedSubcategories, setExpandedSubcategories] = useState<Set<string>>(new Set())
    
    // state for parent filters (buttons with subcategories that act as "show all")
    const [parentFilters, setParentFilters] = useState<{
        scientific: string[]
    }>({
        scientific: []
    })

    // toggle sidebar item expansion
    const toggleSidebarItem = (itemKey: string) => {
        setExpandedItems(prev => 
            prev.includes(itemKey)
                ? prev.filter(key => key !== itemKey)
                : [...prev, itemKey]
        )
    }

    // toggle subcategory expansion
    const toggleSubcategory = (categoryKey: string, optionIndex: number) => {
        const subcategoryKey = `${categoryKey}-${optionIndex}`
        setExpandedSubcategories(prev => {
            const newSet = new Set(prev)
            if (newSet.has(subcategoryKey)) {
                newSet.delete(subcategoryKey)
            } else {
                newSet.add(subcategoryKey)
            }
            return newSet
        })
    }

    // helper functions for subcategories
    const hasSubcategories = (option: any): option is { label: string; subcategories: { label: string; fullValue: string }[] } => {
        return typeof option === 'object' && option !== null && Array.isArray(option.subcategories)
    }

    const getOptionLabel = (option: string | { label: string }): string => {
        return typeof option === 'string' ? option : option.label
    }

    const [activeFilters, setActiveFilters] = useState<{
        administrative: string[]
        scientific: string[]
        place: string[]
    }>({
        administrative: initialValues?.administrative || [],
        scientific: initialValues?.scientific || [],
        place: initialValues?.place || []
    })

    // render sidebar content
    const renderSidebarContent = () => (
        <>
            {/* administrative filter */}
            {administrativeOptions.length > 0 && (
                <div className={clsx(
                    'search-sidebar-employees__item',
                    { active: expandedItems.includes('administrative') }
                )}>
                    <button
                        className='search-sidebar-employees__item__button'
                        type='button'
                        onClick={() => toggleSidebarItem('administrative')}
                    >
                        <ChevronDown />
                        <span className='h5'>
                            {singleStrings?.filterTitles?.administrative?.label}
                        </span>
                    </button>

                    <div className='search-sidebar-employees__item__content'>
                        <div>
                            <div>

                                {/* {singleStrings?.filterTitles?.administrative?.viewAll && (
                                    <button
                                        className='body-small active'
                                        type='button'
                                    >

                                        {singleStrings?.filterTitles?.administrative?.viewAll}

                                        <Close />

                                    </button>
                                )} */}

                                {administrativeOptions.map((option, i) => (
                                    <button
                                        key={i}
                                        className={clsx(
                                            'body-small',
                                            { active: activeFilters.administrative.includes(option) }
                                        )}
                                        type='button'
                                        onClick={() => {
                                            setActiveFilters(prev => ({
                                                ...prev,
                                                administrative: prev.administrative.includes(option)
                                                    ? prev.administrative.filter(item => item !== option)
                                                    : [...prev.administrative, option]
                                            }))
                                        }}
                                    >
                                        {option}

                                        {activeFilters.administrative.includes(option) && (
                                            <Close />
                                        )}
                                    </button>
                                ))}

                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* scientific filter */}
            {scientificOptions.length > 0 && (
                <div className={clsx(
                    'search-sidebar-employees__item',
                    { active: expandedItems.includes('scientific') }
                )}>

                    <button
                        className='search-sidebar-employees__item__button'
                        type='button'
                        onClick={() => toggleSidebarItem('scientific')}
                    >

                        <ChevronDown />

                        <span className='h5'>
                            {singleStrings?.filterTitles?.scientific?.label}
                        </span>

                    </button>

                    <div className='search-sidebar-employees__item__content'>
                        <div>
                            <div>

                                {/* {singleStrings?.filterTitles?.scientific?.viewAll && (
                                    <button
                                        className='body-small active'
                                        type='button'
                                    >

                                        {singleStrings?.filterTitles?.scientific?.viewAll}

                                        <Close />

                                    </button>
                                )} */}

                                {scientificOptions.map((option, i) => {
                                    const optionLabel = getOptionLabel(option)
                                    const hasSubcats = hasSubcategories(option)
                                    const isSubcategoryExpanded = expandedSubcategories.has(`scientific-${i}`)
                                    
                                    // check if any subcategory is active (using fullValue)
                                    const fullValues = hasSubcats ? option.subcategories?.map(sub => sub.fullValue) || [] : []
                                    const activeSubcategoriesCount = fullValues.filter(val => 
                                        activeFilters.scientific.includes(val)
                                    ).length
                                    const hasActiveSubcategory = activeSubcategoriesCount > 0
                                    
                                    // For buttons with subcategories, check parentFilters; for simple buttons, check activeFilters
                                    const isParentActive = hasSubcats 
                                        ? parentFilters.scientific.includes(optionLabel)
                                        : activeFilters.scientific.includes(optionLabel)
                                    
                                    const isActive = isParentActive || hasActiveSubcategory || (hasSubcats && isSubcategoryExpanded)

                                    return (
                                        <div
                                            key={i}
                                            className='content-group'
                                        >
                                            <button
                                                className={clsx(
                                                    'body-small',
                                                    { active: isActive },
                                                    hasSubcats && isSubcategoryExpanded && 'has-subcategories-open'
                                                )}
                                                type='button'
                                                onClick={() => {
                                                    if (hasSubcats) {
                                                        // toggle subcategory panel
                                                        toggleSubcategory('scientific', i)
                                                        
                                                        if (!isSubcategoryExpanded) {
                                                            // opening - activate parent in parentFilters
                                                            setParentFilters(prev => ({
                                                                ...prev,
                                                                scientific: prev.scientific.includes(optionLabel)
                                                                    ? prev.scientific
                                                                    : [...prev.scientific, optionLabel]
                                                            }))
                                                        } else {
                                                            // closing - clear parent and all subcategories
                                                            setParentFilters(prev => ({
                                                                ...prev,
                                                                scientific: prev.scientific.filter(item => item !== optionLabel)
                                                            }))
                                                            setActiveFilters(prev => ({
                                                                ...prev,
                                                                scientific: prev.scientific.filter(item => !fullValues.includes(item))
                                                            }))
                                                        }
                                                    } else {
                                                        // simple toggle for buttons without subcategories
                                                        setActiveFilters(prev => ({
                                                            ...prev,
                                                            scientific: prev.scientific.includes(optionLabel)
                                                                ? prev.scientific.filter(item => item !== optionLabel)
                                                                : [...prev.scientific, optionLabel]
                                                        }))
                                                    }
                                                }}
                                            >
                                                {optionLabel}

                                                {(isParentActive || hasActiveSubcategory || (hasSubcats && isSubcategoryExpanded)) && (
                                                    <span
                                                        onClick={(e: React.MouseEvent) => {
                                                            e.stopPropagation()
                                                            if (hasSubcats) {
                                                                // clear parent and all subcategories, close panel
                                                                setParentFilters(prev => ({
                                                                    ...prev,
                                                                    scientific: prev.scientific.filter(item => item !== optionLabel)
                                                                }))
                                                                setActiveFilters(prev => ({
                                                                    ...prev,
                                                                    scientific: prev.scientific.filter(item => !fullValues.includes(item))
                                                                }))
                                                                if (isSubcategoryExpanded) {
                                                                    toggleSubcategory('scientific', i)
                                                                }
                                                            } else {
                                                                // clear the simple filter
                                                                setActiveFilters(prev => ({
                                                                    ...prev,
                                                                    scientific: prev.scientific.filter(item => item !== optionLabel)
                                                                }))
                                                            }
                                                        }}
                                                    >
                                                        <Close />
                                                    </span>
                                                )}
                                            </button>

                                            {hasSubcats && isSubcategoryExpanded && (
                                                <div className='subcategories'>
                                                    {option.subcategories?.map((subOption, subIndex) => {
                                                        const isSubActive = activeFilters.scientific.includes(subOption.fullValue)
                                                        return (
                                                            <button
                                                                key={subIndex}
                                                                className={clsx(
                                                                    'body-small',
                                                                    { active: isSubActive }
                                                                )}
                                                                type='button'
                                                                onClick={() => {
                                                                    const isCurrentlyActive = activeFilters.scientific.includes(subOption.fullValue)
                                                                    
                                                                    if (isCurrentlyActive) {
                                                                        // deactivating this subcategory
                                                                        setActiveFilters(prev => ({
                                                                            ...prev,
                                                                            scientific: prev.scientific.filter(item => item !== subOption.fullValue)
                                                                        }))
                                                                        
                                                                        // if no subcategories are active, activate parent to show all
                                                                        const remainingActive = fullValues.filter(val => 
                                                                            val !== subOption.fullValue && activeFilters.scientific.includes(val)
                                                                        )
                                                                        
                                                                        if (remainingActive.length === 0) {
                                                                            setParentFilters(prev => ({
                                                                                ...prev,
                                                                                scientific: [...prev.scientific, optionLabel]
                                                                            }))
                                                                        }
                                                                    } else {
                                                                        // activating this subcategory - remove parent first
                                                                        setParentFilters(prev => ({
                                                                            ...prev,
                                                                            scientific: prev.scientific.filter(item => item !== optionLabel)
                                                                        }))
                                                                        setActiveFilters(prev => ({
                                                                            ...prev,
                                                                            scientific: [...prev.scientific, subOption.fullValue]
                                                                        }))
                                                                    }
                                                                }}
                                                            >
                                                                {subOption.label}

                                                                {isSubActive && (
                                                                    <Close />
                                                                )}
                                                            </button>
                                                        )
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    )
                                })}

                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* place filter */}
            {placeOptions.length > 0 && (
                <div className={clsx(
                    'search-sidebar-employees__item',
                    { active: expandedItems.includes('place') }
                )}>
                    <button
                        className='search-sidebar-employees__item__button'
                        type='button'
                        onClick={() => toggleSidebarItem('place')}
                    >

                        <ChevronDown />

                        <span className='h5'>
                            {singleStrings?.filterTitles?.place?.label}
                        </span>

                    </button>

                    <div className='search-sidebar-employees__item__content'>
                        <div>
                            <div>
                                {placeOptions.map((option, i) => (
                                    <button
                                        key={i}
                                        className={clsx(
                                            'body-small',
                                            { active: activeFilters.place.includes(option) }
                                        )}
                                        type='button'
                                        onClick={() => {
                                            setActiveFilters(prev => ({
                                                ...prev,
                                                place: prev.place.includes(option)
                                                    ? prev.place.filter(item => item !== option)
                                                    : [...prev.place, option]
                                            }))
                                        }}
                                    >
                                        {option}

                                        {activeFilters.place.includes(option) && (
                                            <Close />
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )

    const [searchValue, setSearchValue] = useState(initialValues?.search || '')
    const [currentPage, setCurrentPage] = useState(1)
    const [loadMore, setLoadMore] = useState(false)
    const [employees, setEmployees] = useState<any[]>([])
    const [hasNextPage, setHasNextPage] = useState(false)
    const [total, setTotal] = useState(0)
    const [isLoading, setIsLoading] = useState(false)

    // Animation hook
    const { containerRef } = useResultsAnimation(
        [employees, activeFilters],
        { duration: 0.4, stagger: 0.05 }
    )
    
    async function handleFetchEmployees() {
        if (!services?.getEmployees) {
            // Apply mock data to prototype example
            setEmployees((mockData || []).slice(0, howManyPerPage))
            setHasNextPage(mockData?.length as number > howManyPerPage)
            setTotal(mockData?.length || 0)
            setIsLoading(false)
            return
        }

        try {
            const url = new URL(services.getEmployees)
            const queryParams = new URLSearchParams()

            queryParams.set('page', currentPage.toString())
            queryParams.set('pageSize', howManyPerPage.toString())
            queryParams.set('search', searchValue)

            activeFilters.administrative.forEach((filter: string) => {
                queryParams.append('administrative', filter)
            })
            
            // Handle scientific filters with parent logic
            const scientificFiltersToSend: string[] = []
            
            // Check if parent "Lærerutdanning" is active (show all mode)
            if (parentFilters.scientific.includes(singleStrings?.teacherPrefix as string)) {
                const laerutdanningOption = scientificOptions.find(opt => 
                    hasSubcategories(opt) && opt.label === singleStrings?.teacherPrefix as string
                )
                
                if (laerutdanningOption && hasSubcategories(laerutdanningOption)) {
                    // Parent is active - add all subcategories
                    laerutdanningOption.subcategories.forEach(sub => {
                        scientificFiltersToSend.push(sub.fullValue)
                    })
                }
            }
            
            // Add all active filters
            activeFilters.scientific.forEach((filter: string) => {
                scientificFiltersToSend.push(filter)
            })
            
            scientificFiltersToSend.forEach((filter: string) => {
                queryParams.append('scientific', filter)
            })
            
            activeFilters.place.forEach((filter: string) => {
                queryParams.append('place', filter)
            })

            url.search = queryParams.toString()

            updateCurrentUrl()

            const response = await fetch(url);

            if (!response.ok) {
                throw new Error(`Failed to fetch employees: ${response.statusText}`);
            }

            const data = await response.json();

            if (loadMore) {
                setEmployees([...employees, ...data.hits])
                setLoadMore(false)
            } else {
                setEmployees(data.hits)
            }

            setHasNextPage(data.hasNextPage)
            setTotal(data.total)
        } catch(err) {
            console.error('Error while fetch employees', err)
        } finally {
            setIsLoading(false)
        }
    }

    const updateCurrentUrl = () => {
        const origin = window.location.origin;
        const updatedUrl = new URL(`${origin}${window.location.pathname}`);
        const queryParams = new URLSearchParams();

        if (searchValue) queryParams.append('search', searchValue);
        (activeFilters.administrative || []).forEach((filter: string) => {
            queryParams.append('administrative', filter);
        });
        
        // Handle scientific filters with parent logic (same as in handleFetchEmployees)
        const scientificFiltersToSend: string[] = []
        
        // Check if parent "Lærerutdanning" is active (show all mode)
        if (parentFilters.scientific.includes(singleStrings?.teacherPrefix as string)) {
            const laerutdanningOption = scientificOptions.find(opt => 
                hasSubcategories(opt) && opt.label === singleStrings?.teacherPrefix as string
            )
            
            if (laerutdanningOption && hasSubcategories(laerutdanningOption)) {
                // Parent is active - add all subcategories
                laerutdanningOption.subcategories.forEach(sub => {
                    scientificFiltersToSend.push(sub.fullValue)
                })
            }
        }
        
        // Add all active filters
        activeFilters.scientific.forEach((filter: string) => {
            scientificFiltersToSend.push(filter)
        })
        
        scientificFiltersToSend.forEach((filter: string) => {
            queryParams.append('scientific', filter);
        });
        
        (activeFilters.place || []).forEach((filter: string) => {
            queryParams.append('place', filter);
        });

        updatedUrl.search = queryParams.toString();
        window.history.pushState({}, '', updatedUrl);
    }

    const handleSearch = () => {
        setEmployees([])
        setIsLoading(true)
        if (currentPage > 1) {
            setCurrentPage(1)
        } else {
            handleFetchEmployees()
        }
    }

    const handleLoadMore = () => {
        if (hasNextPage) {
            setLoadMore(true)
            setCurrentPage(currentPage + 1)
        }
    }

    const handleClearSearch = () => {
        setSearchValue('')
        setActiveFilters({
            administrative: [],
            scientific: [],
            place: []
        })
        setParentFilters({
            scientific: []
        })
    }

    const handleInputSearch = () => {
        handleSearch()

        // scroll to search results on mobile
        if (window.innerWidth < 992) {
            setTimeout(() => {
                const searchResults = document.getElementById('search-results')
                if (searchResults) {
                    searchResults.scrollIntoView({ 
                        behavior: 'smooth',
                        block: 'start'
                    })
                }
            }, 100)
        }
    }

    // Validate and clean filters from URL on mount
    useEffect(() => {
        if (!initialValues) return

        const validAdministrative: string[] = []
        const validScientific: string[] = []
        const validPlace: string[] = []
        let hasInvalidFilters = false

        // Validate administrative filters
        if (initialValues.administrative && initialValues.administrative.length > 0) {
            const availableAdministrative = filters?.administrative || []
            initialValues.administrative.forEach(filter => {
                if (availableAdministrative.includes(filter)) {
                    validAdministrative.push(filter)
                } else {
                    hasInvalidFilters = true
                }
            })
        }

        // Validate place filters
        if (initialValues.place && initialValues.place.length > 0) {
            const availablePlace = filters?.place || []
            initialValues.place.forEach(filter => {
                if (availablePlace.includes(filter)) {
                    validPlace.push(filter)
                } else {
                    hasInvalidFilters = true
                }
            })
        }

        // Validate scientific filters (including subcategories)
        if (initialValues.scientific && initialValues.scientific.length > 0) {
            const availableScientific = filters?.scientific || []
            const teacherPrefix = singleStrings?.teacherPrefix as string
            
            initialValues.scientific.forEach(filter => {
                // Check if it's a subcategory (starts with teacherPrefix,)
                if (filter.startsWith(`${teacherPrefix},`)) {
                    // Check if this subcategory exists in available filters
                    if (availableScientific.includes(filter)) {
                        validScientific.push(filter)
                    } else {
                        hasInvalidFilters = true
                    }
                } 
                // Check if it's the standalone teacherPrefix
                else if (filter === teacherPrefix) {
                    // Check if teacherPrefix exists in available filters
                    if (availableScientific.includes(filter)) {
                        validScientific.push(filter)
                    } else {
                        hasInvalidFilters = true
                    }
                }
                // Regular scientific filter
                else {
                    if (availableScientific.includes(filter)) {
                        validScientific.push(filter)
                    } else {
                        hasInvalidFilters = true
                    }
                }
            })
        }

        // If there are invalid filters, update state and URL
        if (hasInvalidFilters) {
            setActiveFilters({
                administrative: validAdministrative,
                scientific: validScientific,
                place: validPlace
            })

            // Update URL to remove invalid filters
            const origin = window.location.origin
            const updatedUrl = new URL(`${origin}${window.location.pathname}`)
            const queryParams = new URLSearchParams()

            if (searchValue) queryParams.append('search', searchValue)
            
            validAdministrative.forEach((filter: string) => {
                queryParams.append('administrative', filter)
            })
            
            validScientific.forEach((filter: string) => {
                queryParams.append('scientific', filter)
            })
            
            validPlace.forEach((filter: string) => {
                queryParams.append('place', filter)
            })

            updatedUrl.search = queryParams.toString()
            window.history.replaceState({}, '', updatedUrl)
        }
    }, []) // Run only once on mount

    useEffect(() => {
        handleFetchEmployees()
    }, [currentPage])

    useEffect(() => {
        handleSearch()
    }, [activeFilters, parentFilters])
    
    // Auto-expand subcategories if URL params contain any subcategories
    useEffect(() => {
        if (initialValues?.scientific && initialValues.scientific.length > 0) {
            // Check if any scientific filter is "Lærerutdanning" or starts with "Lærerutdanning,"
            const hasLaerutdanningFilter = initialValues.scientific.some(filter => 
                filter === singleStrings?.teacherPrefix as string || filter.startsWith(`${singleStrings?.teacherPrefix as string},`)
            )
            
            if (hasLaerutdanningFilter) {
                // Find the index of the Lærerutdanning parent in scientificOptions
                const laerutdanningIndex = scientificOptions.findIndex(opt => 
                    hasSubcategories(opt) && opt.label === singleStrings?.teacherPrefix as string
                )
                
                if (laerutdanningIndex !== -1) {
                    setExpandedSubcategories(prev => {
                        const newSet = new Set(prev)
                        newSet.add(`scientific-${laerutdanningIndex}`)
                        return newSet
                    })
                }
            }
        }
    }, [])
    
    return (
        <div className={clsx('page-search-employees', className)}>

            <Section
                className='page-search-employees__search-box'
                removeMarginTop
            >
                <div className='container'>
                    <RoundedBox bgColor='green-light'>
                        <div className='row'>
                            <div className='col-lg-10 offset-lg-1 col-xl-8 offset-xl-2'>

                                {searchBox?.title && (
                                    <h1 className='h1'>
                                        {searchBox.title}
                                    </h1>
                                )}

                                {searchBox?.searchBar && (
                                    <div
                                        className='page-search-employees__search-box__search-bar'
                                    >

                                        <div className='input-wrapper'>

                                            <Input
                                                placeholder={searchBox.searchBar.placeholder}
                                                size='large'
                                                value={searchValue}
                                                onChange={(e) => setSearchValue(e.target.value)}
                                                onKeyDown={handleInputSearch}
                                            />

                                            {
                                                searchValue && (
                                                    <button
                                                        className='clear'
                                                        type='button'
                                                        aria-label={singleStrings?.clearSearch}
                                                        onClick={handleClearSearch}
                                                    >
                                                        <Close />
                                                    </button>
                                                )
                                            }

                                        </div>

                                        <button
                                            className='button-black'
                                            onClick={handleInputSearch}
                                        >
                                            {searchBox.searchBar.cta}
                                        </button>

                                    </div>
                                )}

                                {searchBox?.ctaText && searchBox?.ctas && (searchBox?.ctas?.length > 0) && (
                                    <div className='page-search-employees__search-box__bottom'>
                                        
                                        <p className='h5'>
                                            {searchBox.ctaText}
                                        </p>

                                        <div className='page-search-employees__search-box__ctas'>
                                            {searchBox?.ctas?.map((item, i) => (
                                                <a
                                                    key={i}
                                                    className='button-hollow-black'
                                                    href={item.url || '#'}
                                                    target={item.external ? '_blank' : undefined}
                                                >
                                                    {item.label}
                                                </a>
                                            ))}
                                        </div>

                                    </div>
                                )}
                                    
                            </div>
                        </div>
                    </RoundedBox>
                </div>
            </Section>

            <Section
                className='page-search-employees__main'
                removeMarginTop
            >
                <div className='container'>
                    
                    <div className='row'>

                        <div className='col-lg-4 search-sidebar-employees'>
                            <div className='search-sidebar-employees__wrapper'>
                                {renderSidebarContent()}
                            </div>
                        </div>

                        <div className='desk-col' />

                        <div className='col-lg-8 results'>

                            <button
                                className='button-black open-mobile-filters'
                                type='button'
                                onClick={openMobileFilters}
                            >
                                {singleStrings?.filterCtaMobile?.filter}
                            </button>

                            <p
                                className='h5 results__total'
                                id='search-results'
                            >
                                {singleStrings?.showing} {total || 0} {singleStrings?.results}
                            </p>

                            <div className='results__list' ref={containerRef}>
                                
                                <div className='results__list__items'>
                                    {
                                        employees.map((item, i) => (
                                            <ContactCard
                                                key={i}
                                                color='beige'
                                                className='results__list__item'
                                                {...item}
                                            />
                                        ))
                                    }
                                </div>

                                {hasNextPage && !isLoading && (
                                    <div className='results__list__load-more'>
                                        
                                        <button
                                            className='button-hollow-black'
                                            type='button'
                                            onClick={handleLoadMore}
                                            disabled={loadMore}
                                        >
                                            <span className='button-hollow-black__text'>
                                                {singleStrings?.loadMore}
                                            </span>

                                            {loadMore && (
                                                <span className='button-hollow-black__loading'>
                                                    <Loading />
                                                </span>
                                            )}
                                        </button>

                                    </div>
                                )}

                            </div>

                        </div>
                        
                    </div>

                </div>
            </Section>

            {isMobileFiltersOpen && (
                <Portal>
                    <div 
                        className='search-filters'
                        onClick={closeMobileFilters}
                    >
                        <div 
                            className='search-filters__modal'
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className='container'>

                                <div className='search-filters__header'>
                                    <button
                                        className='close-button'
                                        type='button'
                                        onClick={closeMobileFilters}
                                        aria-label='Close filters'
                                    >
                                        <CloseLight />
                                    </button>
                                </div>

                                <div className='search-filters__content'>
                                    {renderSidebarContent()}
                                </div>

                                <button
                                    className='button-black search-filters__apply'
                                    type='button'
                                    onClick={closeMobileFilters}
                                >
                                    {singleStrings?.filterCtaMobile?.apply}
                                </button>

                            </div>
                        </div>
                    </div>
                </Portal>
            )}

        </div>
    )
}